"use client";

import React from "react";
import { Header, BottomTab } from "components/common";
import { UnAuthRedirect, AuthRedirect } from "components/common/AppRedirect";

interface LayoutProps {
  children: React.ReactNode;
  header?: "default" | "detail" | "chat" | "account" | false;
  headerProps?: any;
  bottomTab?: boolean;
  requireAuth?: boolean;
  requireGuest?: boolean; // For pages like login that should redirect if authenticated
  className?: string;
  contentClassName?: string; // Additional classes for main content area
}

export default function Layout({
  children,
  header = false,
  headerProps = {},
  bottomTab = false,
  requireAuth = false,
  requireGuest = false,
  className = "",
  contentClassName = "",
}: LayoutProps) {
  const content = (
    <div className="flex justify-center full-viewport bg-gray-50">
      <div
        className={`
          flex flex-col
          full-viewport
          w-full
          max-w-[768px]
          bg-white
          relative
          overflow-hidden
          shadow-[0_0_24px_rgba(0,0,0,0.08)]
          ${className}
        `}
      >
      {/* Header - Fixed height with Tailwind styling */}
      {header && (
        <div className="flex-shrink-0 z-10">
          <Header type={header} {...headerProps} />
        </div>
      )}

      {/* Main content - Flexible height with optimized scroll performance */}
      <div
        className={`
          flex-1
          min-h-0
          w-full
          scroll-y
          scroll-container
          scrollbar-hide
          ${contentClassName}
        `}
      >
        <div className="w-full h-full">
          {children}
        </div>
      </div>

      {/* BottomTab - Fixed height with Tailwind styling and safe area padding */}
      {bottomTab && (
        <div
          className="flex-shrink-0 z-10 border-t border-gray-200 transform-gpu bg-white"
          style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
        >
          <BottomTab />
        </div>
      )}
      </div>
    </div>
  );

  // Wrap with auth redirect if required
  if (requireAuth) {
    return (
      <UnAuthRedirect params={{ pathname: typeof window !== "undefined" ? window.location.pathname : "/" }}>
        {content}
      </UnAuthRedirect>
    );
  }

  // Redirect authenticated users away from guest-only pages (like login)
  if (requireGuest) {
    return <AuthRedirect>{content}</AuthRedirect>;
  }

  return content;
}